import type {Three as THREE} from "#three-texture-streaming/three-exports"
import {ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import type {Size2Like} from "@cm/math"
import type {AtlasFormat} from "#three-texture-streaming/texture-tile-constants"
import {TEXTURE_TILE_MARGIN, TEXTURE_TILE_SIZE} from "#three-texture-streaming/texture-tile-constants"
import type {ExplicitTextureStreamingMode} from "#three-texture-streaming/types"
import type {TextureTileId} from "#three-texture-streaming/texture-tile-id"
import type {ThreeTextureTileAtlasCallback} from "#three-texture-streaming/three-texture-tile-atlas-callback"
import type {AtlasTileInfo} from "#three-texture-streaming/three-texture-tile-atlas"
import {ThreeTextureTileAtlas} from "#three-texture-streaming/three-texture-tile-atlas"
import type {ThreeViewportTextureTileUpdater} from "#three-texture-streaming/three-viewport-texture-tile-updater"
import type {IWebgl2RgbaStaging} from "#three-texture-streaming/i-webgl2-rgba-staging"
import type {PixelTileResponse} from "#three-texture-streaming/tile-request-response"

export class ThreeTextureTileAtlasManager {
    constructor(
        readonly renderer: THREE.WebGPURenderer,
        private readonly callback: ThreeTextureTileAtlasCallback,
        private readonly createWebgl2RgbaStaging?: (width: number, height: number) => IWebgl2RgbaStaging,
    ) {}

    dispose(): void {
        for (const atlas of this._atlasByFormat.values()) {
            atlas.dispose()
        }
    }

    readonly tileSize: Size2Like = {width: TEXTURE_TILE_SIZE, height: TEXTURE_TILE_SIZE}
    readonly tileMargin = TEXTURE_TILE_MARGIN

    getAtlasTextureNode(format: AtlasFormat): THREE.TextureNode {
        this.getAtlas(format) // ensure the atlas exists
        return this._atlasTextureNodeByFormat.get(format)!
    }

    registerViewportSize(viewport: ThreeViewportTextureTileUpdater, size: Size2Like): void {
        this._sizeByViewport.set(viewport, size)
    }

    unregisterViewport(viewport: ThreeViewportTextureTileUpdater): void {
        this._sizeByViewport.delete(viewport)
    }

    addTile(
        format: AtlasFormat,
        textureTileId: TextureTileId,
        tileImage: PixelTileResponse,
        coarserTileImage: PixelTileResponse | null,
        lockTile: boolean,
        frame: number,
    ): AtlasTileInfo {
        const atlas = this.getAtlas(format)
        if (atlas.willOverflowOnNextAdd(frame)) {
            this.expandAtlas(atlas, atlas.capacity + 1)
        }
        const atlasTileInfo = atlas.addTile(textureTileId, tileImage, coarserTileImage, lockTile, frame)
        this._atlasByTextureTileId.set(textureTileId, atlas)
        return atlasTileInfo
    }

    setFiltering(linear: boolean): void {
        for (const atlas of this._atlasByFormat.values()) {
            atlas.setFiltering(linear)
        }
    }

    setStreamingMode(streamingMode: ExplicitTextureStreamingMode): void {
        this._streamingMode = streamingMode
    }

    get streamingMode(): ExplicitTextureStreamingMode {
        return this._streamingMode
    }

    setMaxPixelsPerTexel(maxPixelsPerTexel: number): void {
        this._maxPixelsPerTexel = maxPixelsPerTexel
    }

    get maxPixelsPerTexel(): number {
        return this._maxPixelsPerTexel
    }

    setMinCapacity(format: AtlasFormat, minCapacity: number): void {
        const atlas = this.getAtlas(format)
        if (atlas.capacity < minCapacity) {
            this.expandAtlas(atlas, minCapacity)
        }
    }

    removeTile(textureTileId: TextureTileId): void {
        const atlas = this._atlasByTextureTileId.get(textureTileId)
        if (!atlas) {
            throw new Error("ThreeTextureTileAtlasManager: tile not tracked")
        }
        atlas.removeTile(textureTileId)
        this._atlasByTextureTileId.delete(textureTileId)
    }

    hasTile(textureTileId: TextureTileId): boolean {
        return this._atlasByTextureTileId.has(textureTileId)
    }

    markTileAsUsed(textureTileId: TextureTileId, frame: number): void {
        const atlas = this._atlasByTextureTileId.get(textureTileId)
        if (!atlas) {
            throw new Error("ThreeTextureTileAtlasManager: tile not tracked")
        }
        atlas.markTileAsUsed(textureTileId, frame)
    }

    private getAtlas(format: AtlasFormat): ThreeTextureTileAtlas {
        let atlas = this._atlasByFormat.get(format)
        if (!atlas) {
            const numTilesPerLayer = this.getNumTilesPerLayer()
            const atlasCallback: ThreeTextureTileAtlasCallback = {
                onTileEvictedCallback: (id) => {
                    this._atlasByTextureTileId.delete(id)
                    this.callback.onTileEvictedCallback?.(id)
                },
            }
            atlas = new ThreeTextureTileAtlas(
                this.renderer,
                atlasCallback,
                this.tileSize,
                this.tileMargin,
                format,
                numTilesPerLayer,
                this.createWebgl2RgbaStaging,
            )
            this._atlasByFormat.set(format, atlas)
            this._atlasTextureNodeByFormat.set(format, TSL.texture(atlas.atlasTexture) as THREE.TextureNode)
        }
        return atlas
    }

    private expandAtlas(atlas: ThreeTextureTileAtlas, minNewCapacity: number): void {
        const numTilesPerLayer = this.getNumTilesPerLayer()
        const numLayers = Math.ceil(minNewCapacity / numTilesPerLayer)
        atlas.expand(numTilesPerLayer * numLayers)
        this._atlasTextureNodeByFormat.get(atlas.format)!.value = atlas.atlasTexture
    }

    private getNumTilesPerLayer(): number {
        const getNumberOfLayerTilesByViewportSize = (viewportSize: Size2Like) => {
            const avgTextureTilesPerScreenTile = 15
            const numTilesX = Math.ceil(viewportSize.width / this.tileSize.width)
            const numTilesY = Math.ceil(viewportSize.height / this.tileSize.height)
            return Math.ceil(numTilesX * numTilesY * avgTextureTilesPerScreenTile)
        }
        const numTilesPerLayer = Array.from(this._sizeByViewport.values())
            .map(getNumberOfLayerTilesByViewportSize)
            .reduce((prev, curr) => prev + curr, 0)
        return Math.max(numTilesPerLayer, 100)
    }

    private readonly _atlasByFormat = new Map<AtlasFormat, ThreeTextureTileAtlas>()
    private readonly _atlasTextureNodeByFormat = new Map<AtlasFormat, THREE.TextureNode>()
    private readonly _atlasByTextureTileId = new Map<TextureTileId, ThreeTextureTileAtlas>()
    private readonly _sizeByViewport = new Map<ThreeViewportTextureTileUpdater, Size2Like>()
    private _streamingMode: ExplicitTextureStreamingMode = "high-quality"
    private _maxPixelsPerTexel = 1.0
}
